import { Injectable } from '@angular/core';
import { State, Action, StateContext, Selector, Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import * as moment from 'moment';
import produce from 'immer';
import { orderBy } from 'lodash-es';
import { mapOrderStatusOrders, mapPwpOrders, Order } from '../../models';
import { ApiService, EnvService, SentryService } from '../../services';
import { defaultApiStoreIterableData, ApiStoreData, CcsSentryError, MissingApiRequestDataError } from '../utils';
import { FetchOrdersAndReorders, SetOrderAddresses } from './orders.actions';
import { PatientState } from '../patient/patient.state';

export type OrdersStateModel = ApiStoreData<Order[]>;

@State<OrdersStateModel>({
  name: 'orders',
  defaults: defaultApiStoreIterableData,
})
@Injectable({ providedIn: 'root' })
export class OrdersState {
  displayPharmacyAcivity: boolean = false;
  constructor(
    private apiService: ApiService,
    private store: Store,
    private sentry: SentryService,
    private envService: EnvService
  ) {}

  handleError = this.sentry.handleError;
  handleSuccess = this.sentry.handleSuccess;

  @Selector()
  static activeOrders(state: OrdersStateModel): Order[] {
    // Active orders are any orders that have not been shipped or are shipping within the next 30 days

    return orderBy(
      state.data.filter((o) => {
        const hasNotShipped = !o.isShipped;
        const hasRecentlyShipped = moment(o.targetedShipDate)
          .clone()
          .startOf('day')
          .isSameOrAfter(moment().subtract(30, 'days').startOf('day'));
        return hasNotShipped || hasRecentlyShipped;
      }),
      'targetedShipDate',
      'desc'
    );
  }

  @Selector()
  static completedOrders(state: OrdersStateModel): Order[] {
    const activeOrders = this.activeOrders(state);

    return orderBy(
      state.data.filter((o) => !activeOrders.map((ao) => ao.id).includes(o.id)),
      'targetedShipDate',
      'desc'
    );
  }

  @Selector()
  static allOrders(state: OrdersStateModel): Order[] {
    return state.data;
  }

  @Selector()
  static isLoading(state: OrdersStateModel): boolean {
    return state.isLoading;
  }

  @Selector()
  static error(state: OrdersStateModel): CcsSentryError | null {
    return state.error;
  }

  @Action(FetchOrdersAndReorders)
  fetchOrdersAndReorders(ctx: StateContext<OrdersStateModel>) {
    //this.store.selectSnapshot(PatientState.patientStateIsPharmacyPatient);

    const patientModel = this.store.selectSnapshot(PatientState.patient);
    const isPharmacyActivity = patientModel?.isPharmacyPatient;

    if (this.envService.features['newLeafPharmacy']?.enabled && isPharmacyActivity) {
      this.displayPharmacyAcivity = true;
    }
    ctx.setState(
      produce((draft) => {
        draft.isLoading = true;
      })
    );

    const patientId = this.store.selectSnapshot(PatientState.selectedPatientId);

    if (!patientId) {
      const error = new MissingApiRequestDataError(['Missing patient id']);
      return this.handleError({ ctx, error, stateKey: null });
    }

    const processOrderData = (data: Order[]) => {
      const hasOrdersToAuthorize = data && data.some((o) => o.needsAuthorization);
      // There is a bug in the library's gtag set() function. Using gtag on window straight.
      // TODO Refine this later by replacing the gtag library we are using
      (window as any).gtag('set', this.envService.googleAnalytics.measurementId, {
        has_orders_to_authorize: hasOrdersToAuthorize,
      });
      this.handleSuccess({ ctx, data, stateKey: null });
    };

    return this.apiService.getOrderStatusOrders(patientId).pipe(
      tap({
        next: (response) => {
          const data = mapOrderStatusOrders(response.data, this.displayPharmacyAcivity);
          processOrderData(data);
        },
      }),
      catchError((error: Error): Observable<any> => this.handleError({ ctx, error, stateKey: null }))
    );
  }

  @Action(SetOrderAddresses)
  setOrderAddresses(ctx: StateContext<OrdersStateModel>, action: SetOrderAddresses) {
    ctx.setState(
      produce((draft) => {
        action.formValues.orderUpdates.forEach((ou) => {
          if (ou.updateAddress) {
            const correspondingOrder = draft.data.find((o) => o.id === ou.orderId);

            if (correspondingOrder) {
              correspondingOrder.shippingAddress = action.newAddress;
            }
          }
        });
      })
    );
  }
}
